/**
 * State-driven env-var resolution for `cdkl invoke --from-state`.
 *
 * The PR 1 env-resolver classifies any non-literal env-var value (a CFn
 * intrinsic like `Ref` / `Fn::GetAtt` / `Fn::Sub`) as "unresolved" and
 * drops it. That's correct when there's no source of truth for the
 * deployed value — which is also the SAM behavior — but it's wrong when
 * cdk-local has already deployed the stack and the AWS-current physical IDs
 * sit in the cdk-local state file.
 *
 * `--from-state` closes that gap: it loads the host's S3 state for the target
 * stack and substitutes `Ref` / `Fn::GetAtt` / `Fn::Sub` placeholders in
 * the function's `Properties.Environment.Variables` with the deployed
 * values. The result feeds back into the existing
 * `resolveEnvVars(...)` pipeline so `--env-vars` overrides still take
 * precedence — that ordering matters because users routinely override a
 * single variable while using `--from-state` to recover the rest.
 *
 * Scope:
 *
 *   - `Ref: <LogicalId>` — substituted with `state.resources[id].physicalId`.
 *   - `Fn::GetAtt: [<LogicalId>, <attr>]` (and the `"LogicalId.attr"`
 *     string form) — substituted with
 *     `state.resources[id].attributes[attr]`. We deliberately do NOT
 *     synthesize attributes the provider would normally compute (e.g.
 *     IAM role ARNs derived from physicalId + accountId) — `--from-state`
 *     surfaces only what cdk-local recorded at deploy time, which is what the
 *     deployed Lambda's env actually saw.
 *   - `Fn::Sub: '<template>'` (and the two-argument `[template, vars]`
 *     form) — `${LogicalId}` / `${LogicalId.attr}` placeholders are
 *     substituted in place; pseudo parameters (`${AWS::AccountId}` /
 *     `${AWS::Region}` / `${AWS::Partition}` / `${AWS::URLSuffix}`) are
 *     substituted from the optional `pseudoParameters` bag; unrelated
 *     placeholders (mapping references, parameters) are left untouched
 *     and the value is treated as unresolved.
 *   - `Fn::Join: [<delimiter>, [<elements>...]]` — every element is
 *     recursively resolved via this same module and joined with the
 *     delimiter. Closes Gap 1 of issue #286 (the SSM Parameter
 *     `ecs.Secret.fromSsmParameter` shape CDK synthesizes is
 *     `Fn::Join` with pseudo-parameter `Ref`s + a `Ref` to the
 *     parameter; without Fn::Join support the secret silently drops).
 *   - `Fn::Select: [<index>, <list>]` — picks the indexed element of an
 *     array. Index may be a number or a numeric string (CFn templates
 *     often carry the string form). The list argument may be a literal
 *     array of intrinsics OR an intrinsic that resolves to a
 *     `string[]` (today: only `Fn::Split`). Out-of-bounds / negative /
 *     non-finite index reports unresolved. Closes issue #636 — the
 *     `Fn.select(N, Fn.split(':', secret.secretArn))` shape CDK
 *     synthesizes for parsing secret ARN segments.
 *   - `Fn::Split: [<delimiter>, <string>]` — splits a string into a
 *     `string[]`. **Only valid INSIDE `Fn::Select`** — at the env-var
 *     top level the result is an array which can't be an env-var
 *     value, so the top-level dispatcher reports unresolved with a
 *     clear reason.
 *   - `Ref: AWS::AccountId` / `AWS::Region` / `AWS::Partition` /
 *     `AWS::URLSuffix` — substituted from the optional
 *     `pseudoParameters` bag the caller supplies. When the bag is
 *     missing (or the specific key isn't set), the placeholder reports
 *     unresolved — same warn-and-drop policy as every other miss.
 *
 * Cross-stack intrinsics (when the caller supplies a `crossStackResolver`
 * on the `SubstitutionContext` via the async API):
 *
 *   - `Fn::ImportValue: '<exportName>'` (or an intrinsic-valued argument
 *     that resolves to a string against state + pseudo parameters) —
 *     looked up via `crossStackResolver.resolveImport(exportName)`. The
 *     resolver typically reads the host's persistent exports index,
 *     falling back to a per-stack state.json scan on index miss
 *     (closes issue #454).
 *   - `Fn::GetStackOutput: { StackName, OutputName, Region? }` — looked
 *     up via `crossStackResolver.resolveGetStackOutput(stackName, region,
 *     outputName)`. The resolver typically reads the producer stack's
 *     state.json from S3 directly. `Region` defaults to the consumer's
 *     deploy region when omitted.
 *
 *   Both resolvers return `string | undefined`; an `undefined` value
 *   reports unresolved per the standard warn-and-drop policy. Cross-account
 *   `Fn::GetStackOutput.RoleArn` is rejected at the resolver layer (cdk-local
 *   uses S3 state, not CloudFormation; cross-account would require
 *   assuming the role and reading the producer account's separate state
 *   bucket — tracked under #449).
 *
 *   Only the async API (`substituteAgainstStateAsync` /
 *   `substituteEnvVarsFromStateAsync`) handles these. The legacy sync API
 *   surfaces them as `unresolved` ("unsupported intrinsic") so existing
 *   callers (e.g. `ecs-task-resolver.ts`'s sync container parsing) stay
 *   unchanged and the per-key warn-and-drop UX still fires.
 *
 * Out of scope (deferred):
 *
 *   - Cross-region `Fn::ImportValue` (tracked under #451).
 *   - Cross-account `Fn::GetStackOutput.RoleArn` (tracked under #449).
 *   - Other intrinsics (`Fn::If`, `Fn::FindInMap`, etc.). Anything
 *     beyond the supported set is reported as unresolved and the env
 *     var is dropped, matching PR 1's "warn-and-drop" semantics.
 *
 * Failure mode: per-key best-effort. When a substitution can't be
 * produced (state missing for the referenced logical ID, attribute not
 * captured at deploy time, unsupported intrinsic in `Fn::Sub`), the key
 * is reported as unresolved and the caller drops it from the env block
 * with a warn. We never throw out of substitution — a bad reference in
 * one env var must not abort the whole `cdkl invoke` call.
 */

import { describeAwsFailureForWarn, flattenToOneLine } from './credential-error.js';
import type { ResourceState } from '../types/state.js';

/**
 * Result of substituting a single env-var value against state.
 *
 * The discriminated union is load-bearing: callers decide whether to
 * pass the substituted value into the regular env-resolver pipeline
 * (which then accepts it as a literal) or to drop the key with a warn.
 */
export type StateSubstitutionResult =
  | { kind: 'literal'; value: string | number | boolean }
  | { kind: 'unresolved'; reason: string };

/**
 * AWS pseudo parameters supplied by the caller. When set, `Ref: AWS::*`
 * and `${AWS::*}` placeholders inside `Fn::Sub` / `Fn::Join` bodies are
 * substituted from this bag. The CLI layer typically derives every
 * field from the resolved region + an `sts:GetCallerIdentity` call
 * (see `derivePartitionAndUrlSuffix` in `intrinsic-image.ts`, which
 * `ecs-task-resolver.ts` re-exports).
 *
 * Every key is optional; a missing key reports unresolved per the
 * standard warn-and-drop policy.
 */
export interface PseudoParameters {
  accountId?: string;
  region?: string;
  partition?: string;
  urlSuffix?: string;
}

/**
 * Cross-stack lookups consulted by the async substitution path when
 * `Fn::ImportValue` / `Fn::GetStackOutput` are encountered. The async API
 * awaits these — the legacy sync API ignores the field entirely and
 * surfaces both intrinsics as `unresolved`.
 *
 * Both methods return `string | undefined`:
 *   - `string` — the resolved value is substituted into the env-var.
 *   - `undefined` — the per-key warn-and-drop path fires (e.g. the
 *     producer stack has not been deployed yet, the export name has no
 *     index entry, etc.).
 *
 * Implementations are expected to be best-effort and never throw the
 * caller's invoke off the rails. A genuine AWS failure (missing
 * credentials, S3 access denied) is best surfaced as `undefined` so the
 * dropped env-var carries a clear per-key reason instead of aborting the
 * whole substitution pass.
 */
export interface CrossStackResolver {
  /**
   * Look an export by name against the consumer's region. Implementations
   * typically consult the host's persistent exports index, falling back
   * to a per-stack `state.json` scan on miss.
   */
  resolveImport(exportName: string): Promise<string | undefined>;
  /**
   * Look an output by `(producerStack, producerRegion, outputName)`. The
   * resolver is the canonical "explicit producer" path — no Export
   * declaration on the producer side is required. `producerRegion`
   * defaults to the consumer's deploy region when the caller did not
   * supply `Region` on the intrinsic.
   */
  resolveGetStackOutput(
    producerStack: string,
    producerRegion: string,
    outputName: string
  ): Promise<string | undefined>;
}

export interface SubstitutionContext {
  /** State-recorded resources for `Ref` / `Fn::GetAtt` / `${LogicalId}` lookups. */
  resources: Record<string, ResourceState>;
  /**
   * Resolved CloudFormation template `Parameters`, keyed by parameter
   * logical ID. Consulted by `Ref` (and `${LogicalId}` inside `Fn::Sub`)
   * when the logical ID is not a resource — covers
   * `AWS::SSM::Parameter::Value<String>` parameters (what CDK synthesizes
   * for `ssm.StringParameter.valueForStringParameter(...)`), which are
   * CloudFormation PARAMETERS, not resources, so they never appear in
   * `resources` (built from `ListStackResources`). The CLI layer resolves
   * these values out-of-band via SSM Parameter Store (see
   * `ssm-parameter-resolver.ts`) and threads the map in here. Optional;
   * absent when no SSM-backed parameter was resolved.
   */
  parameters?: Record<string, string>;
  /**
   * Logical IDs of `parameters` entries whose value is sensitive — today
   * the decrypted `SecureString` SSM parameters resolved under
   * `--from-cfn-stack` (issue #99). When a `Ref` resolves to one of these,
   * {@link onSensitiveParameterConsumed} fires so the caller can route the
   * consuming env key through docker's value-from-process-env form and keep
   * the secret off the `docker run` argv. Optional; absent when no
   * sensitive parameter was resolved.
   */
  sensitiveParameters?: ReadonlySet<string>;
  /**
   * Called whenever a `Ref` resolves to a logical ID listed in
   * {@link sensitiveParameters} — including refs buried inside `Fn::Join` /
   * `Fn::Sub` (the combinators recurse through the same context, so the
   * composed value that embeds the secret triggers it too). The env-var
   * helpers install a per-key sink so they can mark which output keys
   * consumed a sensitive parameter. Optional; ignored when unset.
   */
  onSensitiveParameterConsumed?: (logicalId: string) => void;
  /** Optional pseudo-parameter bag for AWS::* placeholders. */
  pseudoParameters?: PseudoParameters;
  /**
   * Optional cross-stack resolver consumed by the async substitution
   * path for `Fn::ImportValue` / `Fn::GetStackOutput`. When unset (or
   * when the sync API is used), both intrinsics surface as `unresolved`
   * with the standard warn-and-drop semantics.
   */
  crossStackResolver?: CrossStackResolver;
  /**
   * Consumer's own deploy region. Used as the default for
   * `Fn::GetStackOutput.Region` when the intrinsic omits it. The async
   * path falls back to looking the region up off `pseudoParameters.region`
   * when this field is absent.
   */
  consumerRegion?: string;
}

/**
 * Substitute a single env-var / secret-ValueFrom value (which may be a
 * CFn intrinsic) against the provided state-recorded resources map and
 * optional pseudo-parameter bag.
 *
 * Pure / synchronous / no AWS calls. The caller fetches state via
 * `S3StateBackend.getState(...)` once and (when needed) calls
 * `sts:GetCallerIdentity` once for the `accountId`, then feeds both into
 * each intrinsic substitution.
 *
 * Backward compatible: callers may pass `resources` directly (the
 * pre-PR shape) and the helper will assume `pseudoParameters` is
 * unset — matching the `cdkl invoke --from-state` v1 contract.
 */
export function substituteAgainstState(
  value: unknown,
  contextOrResources: SubstitutionContext | Record<string, ResourceState>
): StateSubstitutionResult {
  const context: SubstitutionContext = isContext(contextOrResources)
    ? contextOrResources
    : { resources: contextOrResources };

  // Primitives are already literal — nothing to substitute. The caller
  // (`mergeFromStateIntoTemplateEnv`) generally won't reach this path
  // because the env-resolver already keeps literals untouched, but we
  // accept it here so callers can treat the function uniformly.
  if (typeof value === 'string' || typeof value === 'number' || typeof value === 'boolean') {
    return { kind: 'literal', value };
  }

  if (value === null || typeof value !== 'object') {
    return {
      kind: 'unresolved',
      reason: `unsupported value type: ${value === null ? 'null' : typeof value}`,
    };
  }

  const obj = value as Record<string, unknown>;
  const keys = Object.keys(obj);
  if (keys.length !== 1) {
    return {
      kind: 'unresolved',
      reason: `expected an intrinsic with one key, got ${keys.length} keys`,
    };
  }

  const intrinsic = keys[0]!;
  const arg = obj[intrinsic];

  if (intrinsic === 'Ref') {
    return resolveRef(arg, context);
  }
  if (intrinsic === 'Fn::GetAtt') {
    return resolveGetAtt(arg, context);
  }
  if (intrinsic === 'Fn::Sub') {
    return resolveSub(arg, context);
  }
  if (intrinsic === 'Fn::Join') {
    return resolveJoin(arg, context);
  }
  if (intrinsic === 'Fn::Select') {
    return resolveSelect(arg, context);
  }
  if (intrinsic === 'Fn::Split') {
    // `Fn::Split` returns a string[]; an array cannot be an env-var
    // value, so at the top level we always reject it with a clear
    // reason. It's still resolvable inside `Fn::Select` via
    // `resolveAny` below.
    return {
      kind: 'unresolved',
      reason: `Fn::Split returns an array, which is not a valid env-var value (use Fn::Select to pick one element)`,
    };
  }

  return {
    kind: 'unresolved',
    reason: `unsupported intrinsic '${intrinsic}' (supported: Ref, Fn::GetAtt, Fn::Sub, Fn::Join, Fn::Select, Fn::Split)`,
  };
}

function isContext(
  v: SubstitutionContext | Record<string, ResourceState>
): v is SubstitutionContext {
  // SubstitutionContext requires a `resources` key whose value is itself
  // an object; a bare `Record<string, ResourceState>` has logical-ID keys
  // whose values are ResourceState objects (with `physicalId` etc.). The
  // discriminator: if the value has a `resources` field that is itself an
  // object AND lacks the ResourceState-shaped fields, it's a context.
  if (typeof v !== 'object' || v === null) return false;
  const r = (v as Record<string, unknown>)['resources'];
  if (r === undefined) return false;
  if (typeof r !== 'object' || r === null) return false;
  // A ResourceState has `physicalId` + `resourceType` at the top level;
  // a SubstitutionContext's `resources` is a record of ResourceStates,
  // so the value under `resources` typically lacks `physicalId` itself.
  return !('physicalId' in r);
}

function resolveRef(arg: unknown, context: SubstitutionContext): StateSubstitutionResult {
  if (typeof arg !== 'string' || arg.length === 0) {
    return { kind: 'unresolved', reason: `Ref expects a non-empty logical ID, got ${typeof arg}` };
  }
  if (arg.startsWith('AWS::')) {
    return resolvePseudoParameter(arg, context.pseudoParameters);
  }
  const resource = context.resources[arg];
  if (resource) {
    return { kind: 'literal', value: resource.physicalId };
  }
  // Not a resource — try the resolved CloudFormation parameters map. This
  // covers `AWS::SSM::Parameter::Value<String>` parameters (CDK's
  // `ssm.StringParameter.valueForStringParameter(...)` shape), which are
  // CloudFormation PARAMETERS, not resources, and so never appear in
  // `resources` (built from `ListStackResources`). The CLI resolves their
  // values via SSM and feeds them in on `context.parameters`.
  const parameterValue = context.parameters?.[arg];
  if (parameterValue !== undefined) {
    // Flag sensitive (SecureString) parameter consumption so the caller
    // keeps the consuming env key off the `docker run` argv (issue #99).
    // Fires for refs nested in Fn::Join / Fn::Sub too — the combinators
    // recurse through this same resolveRef with the same context.
    if (context.sensitiveParameters?.has(arg)) {
      context.onSensitiveParameterConsumed?.(arg);
    }
    return { kind: 'literal', value: parameterValue };
  }
  return {
    kind: 'unresolved',
    reason:
      `Ref '${arg}': no record in the state source ` +
      `(was the resource deployed, or is it a CloudFormation parameter that could not be resolved — ` +
      `e.g. an SSM-backed AWS::SSM::Parameter::Value parameter whose SSM value was not readable?)`,
  };
}

function resolvePseudoParameter(
  name: string,
  pseudo: PseudoParameters | undefined
): StateSubstitutionResult {
  if (!pseudo) {
    return {
      kind: 'unresolved',
      reason: `Ref '${name}': pseudo parameter not supplied (need an active state source, e.g. --from-cfn-stack)`,
    };
  }
  switch (name) {
    case 'AWS::AccountId':
      if (pseudo.accountId !== undefined) return { kind: 'literal', value: pseudo.accountId };
      break;
    case 'AWS::Region':
      if (pseudo.region !== undefined) return { kind: 'literal', value: pseudo.region };
      break;
    case 'AWS::Partition':
      if (pseudo.partition !== undefined) return { kind: 'literal', value: pseudo.partition };
      break;
    case 'AWS::URLSuffix':
      if (pseudo.urlSuffix !== undefined) return { kind: 'literal', value: pseudo.urlSuffix };
      break;
    default:
      return {
        kind: 'unresolved',
        reason: `Ref '${name}': pseudo parameter not supported (supported: AWS::AccountId, AWS::Region, AWS::Partition, AWS::URLSuffix)`,
      };
  }
  return { kind: 'unresolved', reason: `Ref '${name}': pseudo parameter value not resolved` };
}

function resolveGetAtt(arg: unknown, context: SubstitutionContext): StateSubstitutionResult {
  let logicalId: string;
  let attr: string;
  if (Array.isArray(arg) && arg.length === 2 && typeof arg[0] === 'string') {
    logicalId = arg[0];
    if (typeof arg[1] !== 'string') {
      return {
        kind: 'unresolved',
        reason: `Fn::GetAtt's second arg must be a string attribute name, got ${typeof arg[1]} (nested intrinsics in attribute names are not supported)`,
      };
    }
    attr = arg[1];
  } else if (typeof arg === 'string') {
    const dot = arg.indexOf('.');
    if (dot <= 0 || dot === arg.length - 1) {
      return {
        kind: 'unresolved',
        reason: `Fn::GetAtt string form must be '<LogicalId>.<Attribute>', got '${arg}'`,
      };
    }
    logicalId = arg.slice(0, dot);
    attr = arg.slice(dot + 1);
  } else {
    return {
      kind: 'unresolved',
      reason: `Fn::GetAtt expects [LogicalId, Attribute] or 'LogicalId.Attribute', got ${
        Array.isArray(arg) ? `array of length ${arg.length}` : typeof arg
      }`,
    };
  }

  const resource = context.resources[logicalId];
  if (!resource) {
    return {
      kind: 'unresolved',
      reason: `Fn::GetAtt '${logicalId}.${attr}': no record in the state source`,
    };
  }
  const cached = resource.attributes?.[attr];
  if (cached === undefined) {
    return {
      kind: 'unresolved',
      reason: `Fn::GetAtt '${logicalId}.${attr}': attribute not captured in the state source at deploy time`,
    };
  }
  if (typeof cached === 'string' || typeof cached === 'number' || typeof cached === 'boolean') {
    return { kind: 'literal', value: cached };
  }
  // Object/array attribute values (e.g. `S3CanonicalUserId` is a string,
  // but `Endpoints` on some resources is an object). Lambda env vars
  // have to be flat strings, so we surface them as JSON — same posture
  // CFn / SAM take, and lets the handler re-parse if it knows the shape.
  return { kind: 'literal', value: JSON.stringify(cached) };
}

/**
 * `Fn::Sub` accepts:
 *   - `'a-${LogicalId}-b'`  — single-string form, placeholders against
 *     the template / state / pseudo parameters.
 *   - `['a-${X}-b', { X: <intrinsic-or-literal> }]` — two-arg form, the
 *     map provides override values for placeholders that aren't logical
 *     IDs. We recursively resolve each map value via this same module
 *     so a placeholder bound to `{Ref: ...}` works.
 */
function resolveSub(arg: unknown, context: SubstitutionContext): StateSubstitutionResult {
  let template: string;
  let bindings: Record<string, unknown> = {};

  if (typeof arg === 'string') {
    template = arg;
  } else if (
    Array.isArray(arg) &&
    arg.length === 2 &&
    typeof arg[0] === 'string' &&
    arg[1] !== null &&
    typeof arg[1] === 'object' &&
    !Array.isArray(arg[1])
  ) {
    template = arg[0];
    bindings = arg[1] as Record<string, unknown>;
  } else {
    return {
      kind: 'unresolved',
      reason: `Fn::Sub expects a string or [string, object], got ${
        Array.isArray(arg) ? 'malformed array' : typeof arg
      }`,
    };
  }

  // Walk the template and substitute every `${...}` placeholder. We treat
  // any failure mid-walk as a failure for the whole value — partial
  // substitutions would silently produce wrong env vars.
  const placeholderRegex = /\$\{([^}]+)\}/g;
  const placeholders: string[] = [];
  template.replace(placeholderRegex, (_, key: string) => {
    placeholders.push(key);
    return '';
  });

  // Eagerly resolve every placeholder so we can fail fast with a clean
  // reason instead of leaving a half-rewritten string.
  const resolutions = new Map<string, string>();
  for (const placeholder of placeholders) {
    if (resolutions.has(placeholder)) continue;

    if (placeholder in bindings) {
      const sub = substituteAgainstState(bindings[placeholder], context);
      if (sub.kind !== 'literal') {
        return {
          kind: 'unresolved',
          reason: `Fn::Sub placeholder '\${${placeholder}}': ${sub.reason}`,
        };
      }
      resolutions.set(placeholder, String(sub.value));
      continue;
    }

    // Pseudo parameter (`${AWS::AccountId}` etc.)
    if (placeholder.startsWith('AWS::')) {
      const sub = resolvePseudoParameter(placeholder, context.pseudoParameters);
      if (sub.kind !== 'literal') {
        return {
          kind: 'unresolved',
          reason: `Fn::Sub placeholder '\${${placeholder}}': ${sub.reason}`,
        };
      }
      resolutions.set(placeholder, String(sub.value));
      continue;
    }

    // Not in bindings, not a pseudo → treat as a `${LogicalId}` or `${LogicalId.attr}`.
    const dot = placeholder.indexOf('.');
    if (dot === -1) {
      const sub = resolveRef(placeholder, context);
      if (sub.kind !== 'literal') {
        return {
          kind: 'unresolved',
          reason: `Fn::Sub placeholder '\${${placeholder}}': ${sub.reason}`,
        };
      }
      resolutions.set(placeholder, String(sub.value));
    } else {
      const sub = resolveGetAtt(placeholder, context);
      if (sub.kind !== 'literal') {
        return {
          kind: 'unresolved',
          reason: `Fn::Sub placeholder '\${${placeholder}}': ${sub.reason}`,
        };
      }
      resolutions.set(placeholder, String(sub.value));
    }
  }

  const out = template.replace(placeholderRegex, (_, key: string) => {
    return resolutions.get(key) ?? '';
  });
  return { kind: 'literal', value: out };
}

/**
 * `Fn::Join: [<delimiter>, [<elements>]]` — recursively resolve every
 * element through `substituteAgainstState` and join with the delimiter.
 * Closes the SSM Parameter `ecs.Secret.fromSsmParameter` shape (Gap 1
 * of #286) where CDK synthesizes a `Fn::Join` over pseudo-parameter
 * `Ref`s + a `Ref` to the parameter.
 *
 * String / number / boolean elements pass through as-is; intrinsic
 * elements (`Ref` / `Fn::GetAtt` / nested `Fn::Sub` / nested `Fn::Join`)
 * recurse. Any unresolvable element fails the whole join — partial
 * substitutions would silently produce wrong values.
 */
function resolveJoin(arg: unknown, context: SubstitutionContext): StateSubstitutionResult {
  if (!Array.isArray(arg) || arg.length !== 2 || !Array.isArray(arg[1])) {
    return {
      kind: 'unresolved',
      reason: `Fn::Join expects [delimiter, [elements]], got ${
        Array.isArray(arg) ? `array of length ${arg.length}` : typeof arg
      }`,
    };
  }
  const [delimiterRaw, elements] = arg as [unknown, unknown[]];
  if (typeof delimiterRaw !== 'string') {
    return {
      kind: 'unresolved',
      reason: `Fn::Join delimiter must be a string, got ${typeof delimiterRaw}`,
    };
  }

  // Each element MUST resolve to a scalar — `Fn::Join` of arrays is not
  // a thing in CFn. We deliberately call `substituteAgainstState` (the
  // scalar-only top-level dispatcher) rather than `resolveAny` so that a
  // bare `Fn::Split` element gets the same warn-and-drop UX as at the
  // top level. (Inside `Fn::Select`, `resolveAny` IS used to admit
  // arrays — that asymmetry mirrors `intrinsic-image.ts`.)
  const parts: string[] = [];
  for (let i = 0; i < elements.length; i += 1) {
    const sub = substituteAgainstState(elements[i], context);
    if (sub.kind !== 'literal') {
      return {
        kind: 'unresolved',
        reason: `Fn::Join element [${i}]: ${sub.reason}`,
      };
    }
    parts.push(String(sub.value));
  }
  return { kind: 'literal', value: parts.join(delimiterRaw) };
}

/**
 * `Fn::Select: [<index>, <list>]` — pick the indexed element of an
 * array. Mirrors the semantics of `resolveImageIntrinsic`'s `Fn::Select`
 * branch in `src/local/intrinsic-image.ts` so the two resolvers behave
 * the same way for the same template shape.
 *
 *   - Index may be a number (`0`) OR a numeric string (`'0'`) — CFn
 *     templates often carry the string form after a JSON round-trip.
 *   - List may be a resolved-array intrinsic (today only `Fn::Split`
 *     produces a `string[]`) OR a literal `[...]` array of intrinsics
 *     resolved on the fly.
 *   - Out-of-bounds / negative / non-finite index reports unresolved.
 */
function resolveSelect(arg: unknown, context: SubstitutionContext): StateSubstitutionResult {
  if (!Array.isArray(arg) || arg.length !== 2) {
    return {
      kind: 'unresolved',
      reason: `Fn::Select expects [index, list], got ${
        Array.isArray(arg) ? `array of length ${arg.length}` : typeof arg
      }`,
    };
  }
  const [rawIndex, listArg] = arg as [unknown, unknown];

  let index: number | undefined;
  if (typeof rawIndex === 'number') {
    index = rawIndex;
  } else if (typeof rawIndex === 'string' && /^-?\d+$/.test(rawIndex)) {
    index = Number.parseInt(rawIndex, 10);
  }
  if (index === undefined || !Number.isFinite(index)) {
    return {
      kind: 'unresolved',
      reason: `Fn::Select index must be a finite number (or numeric string), got ${typeof rawIndex}`,
    };
  }
  if (index < 0) {
    return {
      kind: 'unresolved',
      reason: `Fn::Select index must be non-negative, got ${index}`,
    };
  }

  // Resolve the list arg through the array-tolerant helper so a nested
  // `Fn::Split` produces a real `string[]`.
  const list = resolveAny(listArg, context);
  if (list.kind === 'unresolved') {
    return {
      kind: 'unresolved',
      reason: `Fn::Select list: ${list.reason}`,
    };
  }

  if (Array.isArray(list.value)) {
    if (index >= list.value.length) {
      return {
        kind: 'unresolved',
        reason: `Fn::Select index ${index} out of bounds (list length ${list.value.length})`,
      };
    }
    const picked = list.value[index]!;
    return { kind: 'literal', value: picked };
  }

  // The list arg resolved to a scalar — that's not a valid list. Most
  // common cause: the template author passed a single intrinsic that
  // resolves to a string instead of a `Fn::Split` / literal array.
  return {
    kind: 'unresolved',
    reason: `Fn::Select list must resolve to an array, got ${typeof list.value}`,
  };
}

/**
 * `Fn::Split: [<delimiter>, <string>]` — split a string into a
 * `string[]`. Only callable through `resolveAny` (i.e. inside
 * `Fn::Select`); the top-level dispatcher rejects bare `Fn::Split`
 * since an array cannot be an env-var value.
 *
 * The string argument can itself be an intrinsic (typical CDK shape:
 * `Fn::Split: [':', { 'Fn::GetAtt': [<Secret>, 'SecretArn'] }]`); it's
 * resolved through `substituteAgainstState` so we don't accidentally
 * admit nested-array shapes there.
 */
function resolveSplitAsArray(
  arg: unknown,
  context: SubstitutionContext
): { kind: 'literal'; value: string[] } | { kind: 'unresolved'; reason: string } {
  if (!Array.isArray(arg) || arg.length !== 2) {
    return {
      kind: 'unresolved',
      reason: `Fn::Split expects [delimiter, string], got ${
        Array.isArray(arg) ? `array of length ${arg.length}` : typeof arg
      }`,
    };
  }
  const [delim, src] = arg as [unknown, unknown];
  if (typeof delim !== 'string') {
    return {
      kind: 'unresolved',
      reason: `Fn::Split delimiter must be a string, got ${typeof delim}`,
    };
  }
  const sub = substituteAgainstState(src, context);
  if (sub.kind !== 'literal') {
    return {
      kind: 'unresolved',
      reason: `Fn::Split string argument: ${sub.reason}`,
    };
  }
  if (typeof sub.value !== 'string') {
    return {
      kind: 'unresolved',
      reason: `Fn::Split string argument must resolve to a string, got ${typeof sub.value}`,
    };
  }
  return { kind: 'literal', value: sub.value.split(delim) };
}

/**
 * Array-tolerant resolver used by `Fn::Select`'s `list` argument.
 * Returns either the scalar `StateSubstitutionResult` shape (delegating
 * to `substituteAgainstState`) OR a `{kind: 'literal', value: string[]}`
 * when the node is `Fn::Split` / a literal array of intrinsics.
 *
 * Top-level `substituteAgainstState` deliberately doesn't go through
 * this helper — env-var values can't be arrays, and the asymmetry
 * matches `intrinsic-image.ts`'s `resolveImageIntrinsic` (scalar) vs
 * `resolveImageIntrinsicAny` (scalar OR array) split.
 */
function resolveAny(
  value: unknown,
  context: SubstitutionContext
):
  | { kind: 'literal'; value: string | number | boolean | string[] }
  | { kind: 'unresolved'; reason: string } {
  // Literal arrays of intrinsics: resolve each element to a scalar.
  if (Array.isArray(value)) {
    const out: string[] = [];
    for (let i = 0; i < value.length; i += 1) {
      const sub = substituteAgainstState(value[i], context);
      if (sub.kind !== 'literal') {
        return {
          kind: 'unresolved',
          reason: `list element [${i}]: ${sub.reason}`,
        };
      }
      out.push(String(sub.value));
    }
    return { kind: 'literal', value: out };
  }

  // Direct `Fn::Split` intrinsic — produces an array.
  if (
    value !== null &&
    typeof value === 'object' &&
    !Array.isArray(value) &&
    Object.keys(value as Record<string, unknown>).length === 1 &&
    Object.prototype.hasOwnProperty.call(value, 'Fn::Split')
  ) {
    return resolveSplitAsArray((value as Record<string, unknown>)['Fn::Split'], context);
  }

  // Anything else: fall back to the scalar top-level dispatcher.
  return substituteAgainstState(value, context);
}

/**
 * Async sibling of {@link substituteAgainstState}. Same semantics for every
 * intrinsic the sync path supports; additionally consults the
 * `crossStackResolver` (when supplied on the context) for `Fn::ImportValue`
 * and `Fn::GetStackOutput`.
 *
 * Callers that don't need cross-stack support should keep using the sync
 * helper. Code paths that wire `--from-state` env / secret substitution
 * (e.g. `cdkl invoke --from-state`, `cdkl run-task --from-state`)
 * route through this async version so a single env-var referencing a
 * cross-stack output is no longer warn-and-dropped.
 */
export async function substituteAgainstStateAsync(
  value: unknown,
  contextOrResources: SubstitutionContext | Record<string, ResourceState>
): Promise<StateSubstitutionResult> {
  const context: SubstitutionContext = isContext(contextOrResources)
    ? contextOrResources
    : { resources: contextOrResources };

  // Primitives flow through unchanged — same as the sync path.
  if (typeof value === 'string' || typeof value === 'number' || typeof value === 'boolean') {
    return { kind: 'literal', value };
  }

  if (value === null || typeof value !== 'object') {
    return {
      kind: 'unresolved',
      reason: `unsupported value type: ${value === null ? 'null' : typeof value}`,
    };
  }

  const obj = value as Record<string, unknown>;
  const keys = Object.keys(obj);
  if (keys.length !== 1) {
    return {
      kind: 'unresolved',
      reason: `expected an intrinsic with one key, got ${keys.length} keys`,
    };
  }

  const intrinsic = keys[0]!;
  const arg = obj[intrinsic];

  // For every intrinsic the sync path supports, the sync helper is fully
  // sufficient — recurse via the sync path so we don't pay the async
  // overhead on every nested element. The sync helper recurses into
  // bindings / Join elements through the same module, so any cross-stack
  // intrinsic nested under (say) a `Fn::Join` is NOT resolvable today.
  // That's an intentional v1 scope limit: cross-stack intrinsics
  // typically appear at the env-var ROOT, not buried inside a join. We
  // can lift the limit later by replacing the sync recursion below with
  // async recursion when there's a real-world need.
  if (
    intrinsic === 'Ref' ||
    intrinsic === 'Fn::GetAtt' ||
    intrinsic === 'Fn::Sub' ||
    intrinsic === 'Fn::Join' ||
    intrinsic === 'Fn::Select' ||
    intrinsic === 'Fn::Split'
  ) {
    return substituteAgainstState(value, context);
  }

  if (intrinsic === 'Fn::ImportValue') {
    return resolveImportValueAsync(arg, context);
  }
  if (intrinsic === 'Fn::GetStackOutput') {
    return resolveGetStackOutputAsync(arg, context);
  }

  return {
    kind: 'unresolved',
    reason: `unsupported intrinsic '${intrinsic}' (supported: Ref, Fn::GetAtt, Fn::Sub, Fn::Join, Fn::Select, Fn::Split, Fn::ImportValue, Fn::GetStackOutput)`,
  };
}

/**
 * `Fn::ImportValue: <exportName>` — the argument may itself be an
 * intrinsic that resolves to a string (e.g.
 * `{Fn::ImportValue: {Fn::Sub: 'MyStack-${AWS::Region}-Bucket'}}` — the
 * inner `Fn::Sub` is resolved against `pseudoParameters` first, then the
 * resulting string is looked up via the cross-stack resolver).
 */
async function resolveImportValueAsync(
  arg: unknown,
  context: SubstitutionContext
): Promise<StateSubstitutionResult> {
  // Resolve the inner argument first (a string passes through; an
  // intrinsic is resolved against the sync path, which handles every
  // shape an export-name argument might take in practice). We don't
  // recurse into the async path here because the inner part cannot
  // itself be `Fn::ImportValue` — AWS rejects that nesting too.
  const inner = substituteAgainstState(arg, context);
  if (inner.kind !== 'literal') {
    return {
      kind: 'unresolved',
      reason: `Fn::ImportValue argument: ${inner.reason}`,
    };
  }
  if (typeof inner.value !== 'string' || inner.value.length === 0) {
    return {
      kind: 'unresolved',
      reason: `Fn::ImportValue argument must resolve to a non-empty string, got ${typeof inner.value}`,
    };
  }
  const exportName = inner.value;

  if (!context.crossStackResolver) {
    return {
      kind: 'unresolved',
      reason: `Fn::ImportValue '${exportName}': no cross-stack resolver supplied (pass a state-source flag, e.g. --from-cfn-stack, and ensure the producer stack was deployed)`,
    };
  }

  let resolved: string | undefined;
  try {
    resolved = await context.crossStackResolver.resolveImport(exportName);
  } catch (err) {
    // Issue #579, corrected in review round 2. An earlier revision of this
    // comment claimed `resolveImport` IS a CloudFormation `ListExports` call.
    // It is not, and the distinction changes what this `catch` can see:
    // `CrossStackResolver` is a HOST-IMPLEMENTED interface exported from
    // `src/index.ts` (the STABLE surface), and cdk-local's own implementation
    // in `cfn-local-state-provider.ts` catches its SDK failures INTERNALLY
    // (`fetchAllExports(...).catch(...)` warns and resolves `undefined`). So
    // nothing cdk-local throws reaches here at all: what does is a host
    // resolver's throw, or a cdk-local bug.
    //
    // LEVEL: this `reason` is not logged here, but three DEFAULT-level warns
    // print it verbatim (`local-invoke.ts`, `local-start-api.ts`,
    // `local-invoke-agentcore.ts`), and studio mirrors those into its log ring.
    //
    // RECONSTRUCTION is therefore NOT established by inspection, and the
    // withholding default is kept deliberately rather than by default: this
    // interface's whole purpose is resolving DEPLOYED cross-stack state, so an
    // AWS-backed host implementation is the expected case rather than the
    // exotic one, and such a resolver resolves the same credential chain
    // before its request goes out. The discriminator still does the right
    // thing on both populations: a host that lets a modeled service exception
    // escape keeps its message, because `$fault` / `$metadata` are set by the
    // SDK from the response and not by anything the host chose.
    //
    // ACCEPTED COST, stated so it is not rediscovered as a bug: a host
    // resolver's OWN `throw new Error('export index missing for X')` is
    // withheld to a class name plus a length. It is recoverable at `debug`,
    // and the remedy for a host that wants its text on the default line is the
    // one this policy names everywhere else, and which cdk-local applies to
    // its own throws in `layer-arn-materializer.ts` and
    // `httpv2-service-integration.ts`: raise something identifiable and frame
    // the message above the boundary.
    return {
      kind: 'unresolved',
      // `exportName` is flattened for the same one-call reason every other
      // wire-derived value on a default-level line is: it is resolved from the
      // template (and may itself come through `Fn::Sub` against deployed
      // state), and this `reason` is printed verbatim by three warns.
      reason: `Fn::ImportValue '${flattenToOneLine(exportName)}': lookup failed: ${describeAwsFailureForWarn(
        err,
        'CrossStackResolver.resolveImport (Fn::ImportValue)'
      )}`,
    };
  }
  if (resolved === undefined) {
    return {
      kind: 'unresolved',
      reason: `Fn::ImportValue '${exportName}': export not found in any deployed stack in this region`,
    };
  }
  return { kind: 'literal', value: resolved };
}

/**
 * `Fn::GetStackOutput: { StackName, OutputName, Region? }`. Same shape as
 * the deploy-engine resolver. `RoleArn` (cross-account) is intentionally
 * NOT supported in this path — the user-visible error message points at
 * the followup issue tracking it.
 */
async function resolveGetStackOutputAsync(
  arg: unknown,
  context: SubstitutionContext
): Promise<StateSubstitutionResult> {
  if (!arg || typeof arg !== 'object' || Array.isArray(arg)) {
    return {
      kind: 'unresolved',
      reason: `Fn::GetStackOutput argument must be an object with StackName / OutputName / Region, got ${
        arg === null ? 'null' : Array.isArray(arg) ? 'array' : typeof arg
      }`,
    };
  }
  const args = arg as Record<string, unknown>;

  const stackNameSub = substituteAgainstState(args['StackName'], context);
  if (stackNameSub.kind !== 'literal') {
    return {
      kind: 'unresolved',
      reason: `Fn::GetStackOutput.StackName: ${stackNameSub.reason}`,
    };
  }
  if (typeof stackNameSub.value !== 'string' || stackNameSub.value.length === 0) {
    return {
      kind: 'unresolved',
      reason: `Fn::GetStackOutput.StackName must resolve to a non-empty string, got ${typeof stackNameSub.value}`,
    };
  }
  const stackName = stackNameSub.value;

  const outputNameSub = substituteAgainstState(args['OutputName'], context);
  if (outputNameSub.kind !== 'literal') {
    return {
      kind: 'unresolved',
      reason: `Fn::GetStackOutput.OutputName: ${outputNameSub.reason}`,
    };
  }
  if (typeof outputNameSub.value !== 'string' || outputNameSub.value.length === 0) {
    return {
      kind: 'unresolved',
      reason: `Fn::GetStackOutput.OutputName must resolve to a non-empty string, got ${typeof outputNameSub.value}`,
    };
  }
  const outputName = outputNameSub.value;

  let region: string | undefined;
  if (args['Region'] !== undefined && args['Region'] !== null) {
    const regionSub = substituteAgainstState(args['Region'], context);
    if (regionSub.kind !== 'literal') {
      return {
        kind: 'unresolved',
        reason: `Fn::GetStackOutput.Region: ${regionSub.reason}`,
      };
    }
    if (typeof regionSub.value !== 'string' || regionSub.value.length === 0) {
      return {
        kind: 'unresolved',
        reason: `Fn::GetStackOutput.Region must resolve to a non-empty string, got ${typeof regionSub.value}`,
      };
    }
    region = regionSub.value;
  } else {
    region = context.consumerRegion ?? context.pseudoParameters?.region;
  }
  if (!region) {
    return {
      kind: 'unresolved',
      reason: `Fn::GetStackOutput '${stackName}.${outputName}': no Region supplied and consumer region is unknown (set --region, AWS_REGION, or env.region on the CDK stack)`,
    };
  }

  if (args['RoleArn'] !== undefined && args['RoleArn'] !== null) {
    return {
      kind: 'unresolved',
      reason: `Fn::GetStackOutput '${stackName}.${outputName}': RoleArn (cross-account) is not yet supported by the state source — tracked under issue #449`,
    };
  }

  if (!context.crossStackResolver) {
    return {
      kind: 'unresolved',
      reason: `Fn::GetStackOutput '${stackName}.${outputName}': no cross-stack resolver supplied (pass a state-source flag, e.g. --from-cfn-stack, and ensure the producer stack was deployed)`,
    };
  }

  let resolved: string | undefined;
  try {
    resolved = await context.crossStackResolver.resolveGetStackOutput(
      stackName,
      region,
      outputName
    );
  } catch (err) {
    // Issue #579 — the same HOST-BOUNDARY catch as the `Fn::ImportValue` site
    // above, decided the same way and for the same reasons; see the comment
    // there. cdk-local's own `resolveGetStackOutput` warns and returns
    // `undefined` rather than throwing, so this too only ever sees a host
    // resolver's throw.
    return {
      kind: 'unresolved',
      // Same treatment for the three template-derived values here.
      reason: `Fn::GetStackOutput '${flattenToOneLine(stackName)}.${flattenToOneLine(
        outputName
      )}' (${flattenToOneLine(region)}): lookup failed: ${describeAwsFailureForWarn(
        err,
        'CrossStackResolver.resolveGetStackOutput (Fn::GetStackOutput)'
      )}`,
    };
  }
  if (resolved === undefined) {
    return {
      kind: 'unresolved',
      reason: `Fn::GetStackOutput '${stackName}.${outputName}' (${region}): output not found in producer stack state`,
    };
  }
  return { kind: 'literal', value: resolved };
}

/**
 * High-level helper: walk the function's `Properties.Environment.Variables`
 * map and produce a pre-resolved version where every value is a literal
 * (substituted from state when possible). Values that can't be substituted
 * are removed so the downstream `resolveEnvVars` treats them as missing
 * and the caller's existing intrinsic-warn-and-drop path fires.
 *
 * Returns the substitution audit alongside the rewritten map so the CLI
 * layer can log per-key info (`STATE_BUCKET=bucket-1234` / `TABLE_ARN
 * skipped: state missing`).
 */
export interface StateEnvSubstitutionAudit {
  /** Keys whose substitution succeeded — surfaced as literals in `env`. */
  resolvedKeys: string[];
  /**
   * Keys that the substituter could not resolve (state missing,
   * unsupported intrinsic, etc.) — paired with a per-key reason so the
   * CLI layer can warn-and-drop with context.
   */
  unresolved: Array<{ key: string; reason: string }>;
  /**
   * Keys whose resolved value consumed a sensitive (SecureString) parameter
   * (see {@link SubstitutionContext.sensitiveParameters}). The CLI layer
   * routes these through docker's value-from-process-env form so the
   * decrypted secret never lands on the `docker run` argv (issue #99).
   */
  sensitiveKeys: string[];
}

/**
 * Build a pre-substituted env map from the template entry by feeding each
 * intrinsic value through `substituteAgainstState`. Literal entries pass
 * through untouched (the env-resolver handles them).
 *
 * @param templateEnv  The function's `Properties.Environment.Variables`
 *                     map from the synthesized template, or `undefined`
 *                     when the function has no env vars.
 * @param resources    `state.resources` from the host's S3 state file for
 *                     the function's stack.
 */
export function substituteEnvVarsFromState(
  templateEnv: Record<string, unknown> | undefined,
  contextOrResources: SubstitutionContext | Record<string, ResourceState>
): { env: Record<string, unknown>; audit: StateEnvSubstitutionAudit } {
  const env: Record<string, unknown> = {};
  const audit: StateEnvSubstitutionAudit = { resolvedKeys: [], unresolved: [], sensitiveKeys: [] };

  if (!templateEnv) return { env, audit };

  const context: SubstitutionContext = isContext(contextOrResources)
    ? contextOrResources
    : { resources: contextOrResources };

  for (const [key, value] of Object.entries(templateEnv)) {
    // Cheap fast path for already-literal values: no substitution
    // attempted, no audit entry — env-resolver will simply keep them.
    if (typeof value === 'string' || typeof value === 'number' || typeof value === 'boolean') {
      env[key] = value;
      continue;
    }

    const { result, consumedSensitive } = resolveWithSensitivity(value, context);
    if (result.kind === 'literal') {
      env[key] = result.value;
      audit.resolvedKeys.push(key);
      if (consumedSensitive) audit.sensitiveKeys.push(key);
    } else {
      audit.unresolved.push({ key, reason: result.reason });
      // Intentionally drop the key — that way the downstream
      // env-resolver sees "no template value for K" and the existing
      // PR 1 warn-and-drop fires with the same UX.
    }
  }

  return { env, audit };
}

/**
 * Resolve a single intrinsic value while detecting whether its resolution
 * consumed a sensitive (SecureString) parameter — including refs nested in
 * `Fn::Join` / `Fn::Sub`, since the combinators recurse through the same
 * per-key context. Installs a per-call sink on a shallow context copy so
 * the shared input context is never mutated. Used by both the sync and
 * async env-var helpers (the async path delegates these intrinsics to the
 * sync resolver, so the sink fires identically).
 */
export function resolveWithSensitivity(
  value: unknown,
  context: SubstitutionContext
): { result: StateSubstitutionResult; consumedSensitive: boolean } {
  if (!context.sensitiveParameters || context.sensitiveParameters.size === 0) {
    return { result: substituteAgainstState(value, context), consumedSensitive: false };
  }
  let consumedSensitive = false;
  const keyContext: SubstitutionContext = {
    ...context,
    onSensitiveParameterConsumed: () => {
      consumedSensitive = true;
    },
  };
  return { result: substituteAgainstState(value, keyContext), consumedSensitive };
}

/**
 * Async sibling of {@link resolveWithSensitivity}. Routes through
 * {@link substituteAgainstStateAsync} (for `Fn::ImportValue` /
 * `Fn::GetStackOutput`), which delegates SSM-parameter-bearing intrinsics
 * to the sync resolver — so the sensitivity sink fires the same way.
 */
export async function resolveWithSensitivityAsync(
  value: unknown,
  context: SubstitutionContext
): Promise<{ result: StateSubstitutionResult; consumedSensitive: boolean }> {
  if (!context.sensitiveParameters || context.sensitiveParameters.size === 0) {
    return { result: await substituteAgainstStateAsync(value, context), consumedSensitive: false };
  }
  let consumedSensitive = false;
  const keyContext: SubstitutionContext = {
    ...context,
    onSensitiveParameterConsumed: () => {
      consumedSensitive = true;
    },
  };
  return { result: await substituteAgainstStateAsync(value, keyContext), consumedSensitive };
}

/**
 * Async sibling of {@link substituteEnvVarsFromState}. Routes every
 * intrinsic-valued entry through {@link substituteAgainstStateAsync} so
 * `Fn::ImportValue` / `Fn::GetStackOutput` resolve via the context's
 * `crossStackResolver` (when supplied). Mirrors the sync version in every
 * other respect: literal entries pass through unchanged, unresolved
 * entries are dropped with a per-key audit reason, and the env-resolver
 * sees the same "no template value" shape so the warn-and-drop path
 * fires consistently.
 *
 * Closes issue #454 — `cdkl invoke --from-state` and
 * `cdkl run-task --from-state` can now resolve cross-stack output
 * references in env vars / secrets instead of warn-and-dropping them.
 */
export async function substituteEnvVarsFromStateAsync(
  templateEnv: Record<string, unknown> | undefined,
  contextOrResources: SubstitutionContext | Record<string, ResourceState>
): Promise<{ env: Record<string, unknown>; audit: StateEnvSubstitutionAudit }> {
  const env: Record<string, unknown> = {};
  const audit: StateEnvSubstitutionAudit = { resolvedKeys: [], unresolved: [], sensitiveKeys: [] };

  if (!templateEnv) return { env, audit };

  const context: SubstitutionContext = isContext(contextOrResources)
    ? contextOrResources
    : { resources: contextOrResources };

  for (const [key, value] of Object.entries(templateEnv)) {
    if (typeof value === 'string' || typeof value === 'number' || typeof value === 'boolean') {
      env[key] = value;
      continue;
    }

    const { result, consumedSensitive } = await resolveWithSensitivityAsync(value, context);
    if (result.kind === 'literal') {
      env[key] = result.value;
      audit.resolvedKeys.push(key);
      if (consumedSensitive) audit.sensitiveKeys.push(key);
    } else {
      audit.unresolved.push({ key, reason: result.reason });
    }
  }

  return { env, audit };
}

/**
 * Last-resort fill for env keys that static substitution could not
 * resolve, using the consumer resource's already-deployed environment.
 *
 * When a Lambda / container is bound to a deployed CloudFormation stack
 * (`--from-cfn-stack`), the consumer itself is deployed, so CloudFormation
 * already resolved every intrinsic (`Fn::GetAtt`, `Fn::Sub`,
 * `Fn::ImportValue`, cross-stack `Ref`) into its concrete deployed env
 * value. `ListStackResources` does not return per-attribute values, so
 * the static substituter drops those keys — but the deployed env carries
 * the answer. The provider fetches the deployed env (e.g.
 * `lambda:GetFunctionConfiguration`) and this helper splices it in for
 * the keys that remained unresolved.
 *
 * Precedence: only keys in `unresolved` are touched — statically resolved
 * keys and literal keys are left exactly as the substituter produced
 * them, and a `--env-vars` override still wins downstream. Keys absent
 * from `deployedEnv` stay unresolved so the caller's warn-and-drop fires.
 *
 * Pure: returns new arrays / a new env map, never mutates the inputs.
 */
export function applyDeployedEnvFallback(
  resolvedEnv: Record<string, unknown>,
  unresolved: ReadonlyArray<{ key: string; reason: string }>,
  deployedEnv: Record<string, string> | undefined
): {
  env: Record<string, unknown>;
  filled: string[];
  stillUnresolved: Array<{ key: string; reason: string }>;
} {
  if (!deployedEnv) {
    return { env: resolvedEnv, filled: [], stillUnresolved: [...unresolved] };
  }
  const env = { ...resolvedEnv };
  const filled: string[] = [];
  const stillUnresolved: Array<{ key: string; reason: string }> = [];
  for (const item of unresolved) {
    const deployedValue = deployedEnv[item.key];
    if (deployedValue !== undefined) {
      env[item.key] = deployedValue;
      filled.push(item.key);
    } else {
      stillUnresolved.push({ key: item.key, reason: item.reason });
    }
  }
  return { env, filled, stillUnresolved };
}
